import React from 'react'
import styled from '@emotion/styled'
import { array, object } from 'prop-types'

import { SectionTitle } from './styles'
import { BuyingGuides } from './BuyingGuides'
import { AuthorCard, ContentContainer, DatalotBanner } from '@jelly/components'
import { MoreCoverage } from './MoreCoverage'
import { OurApproach } from './OurApproach'
import { OurReviews } from './OurReviews'
import { Pricing } from './Pricing'
import { ProductsServices } from './ProductsServices'
import {
	SECTION_BEST_PICKS,
	SECTION_BUYING_GUIDE,
	SECTION_CONTAINER,
	SECTION_MORECOVERAGE,
	SECTION_OURAPPROACH,
	SECTION_REVIEWS,
	SECTION_SERVICES,
	SECTION_WIDGET,
} from '../constants'

const Content = styled.div`
	width: 100%;
`

/**
 * Main switch function that returns a sectional component based on type
 *
 * @param  {Object} section :: JSON data decribing section
 * @param  {Object} author :: JSON data decribing author
 *
 * @return {Node} React component to render if applicable, null otherwise
 */
const renderSection = ({ section, author, categoryWidgetId, slug }) => {
	switch (section.type) {
		case SECTION_BUYING_GUIDE:
			return <BuyingGuides containers={section.containers} id={section.id} title={section.title} />
		case SECTION_CONTAINER:
			return (
				<Content>
					{section.title && <SectionTitle>{section.title}</SectionTitle>}
					<ContentContainer content={section.container.html} />
				</Content>
			)
		case SECTION_REVIEWS:
			return <OurReviews title={section.title} reviews={section.reviews} />
		case SECTION_WIDGET:
			return <Pricing title={section.title} widgetId={section.widget.widgetId} />
		case SECTION_MORECOVERAGE:
			return (
				<>
					<AuthorCard author={author} />
					{section.articles.length === 3 && (
						<MoreCoverage title={section.title} morecoverage={section.articles} />
					)}
				</>
			)
		case SECTION_OURAPPROACH:
			return (
				<>
					{(slug === 'best-business-liability-insurance' || slug === 'insurance/best') && <DatalotBanner />}
					<OurApproach ourapproach={section.ourapproach} />
				</>
			)
		case SECTION_SERVICES:
			return (
				!!section.services && (
					<ProductsServices
						categoryWidgetId={categoryWidgetId}
						services={section.services}
						title={section.title}
					/>
				)
			)
		default:
			return
	}
}

// Styled Components via Emotion
const SectionContainer = styled.article`
	div {
		overflow: hidden;
		overflow-x: auto;

		::-webkit-scrollbar {
			-webkit-overflow-scrolling: auto;
			-webkit-appearance: none;
			background: #f3f3f3;
			border-radius: 10px;
			height: 8px;
		}
		::-webkit-scrollbar-thumb {
			border-radius: 10px;
			background-color: ${({ theme }) => theme.colors.gray[200]};
		}
	}

	&& table {
		border-width: 0;
		display: table;
		position: relative;
		table-layout: fixed;
		width: 100% !important; /* override CMS hardcoded styles */
	}
	tbody tr td:nth-of-type(odd) {
		background: rgba(242, 245, 247, 0.4);
	}
	tbody tr td:nth-of-type(even) {
		background: rgba(218, 225, 229, 0.4);
	}
	tbody tr td:nth-of-type(1n) {
		width: 300px !important; /* override CMS hardcoded styles */
		${({ theme }) => theme.MQ.M} {
			width: 200px !important; /* override CMS hardcoded styles */
		}
	}
	tbody tr:nth-of-type(1) td {
		background: none;
		color: ${({ theme }) => theme.colors.teal};
		font-weight: 600;
	}
	tbody tr td:nth-of-type(1) {
		background: none;
		font-family: 'Poppins Medium', sans-serif;
		width: 150px !important; /* override CMS hardcoded styles */
		word-break: break-word;
	}

	&& tbody,
	&& td,
	&& tfoot,
	&& th,
	&& thead,
	&& time,
	&& tr,
	&& tt {
		border-width: 5px;
		border-color: #ffffff;
		border-style: solid;
		color: ${({ theme }) => theme.colors.darkGrey};
	}
	margin: ${({ isFirstSection }) => (isFirstSection ? `0 auto 40px auto` : `0 auto`)};
	width: 90%;
	${({ theme }) => theme.MQ.M} {
		width: 100%;
	}
`
const Section = styled.section`
	margin: ${({ isFirstSection }) => (isFirstSection ? `0 auto 40px auto` : `40px auto`)};
	p {
		margin-left: auto;
		margin-right: auto;
		width: 90%;
		${({ theme }) => theme.MQ.M} {
			width: 100%;
		}
	}

	h3 {
		${({ theme }) => theme.fonts.secondary}
		color: ${({ theme }) => theme.colors.navy};
		font-size: 22px;
		font-weight: normal;
		text-transform: uppercase;
	}
`

Sections.propTypes = {
	sections: array.isRequired,
	author: object,
}

/**
 * This is the Sections component that will grab each section based on the given order
 *
 * @param {Array} props.sections :: list of all the sections in order, set in CCM
 */
export function Sections({ sections, author, categoryWidgetId, slug }) {
	const determineSectionStyle = (type) => {
		if (type === SECTION_CONTAINER || type === SECTION_BUYING_GUIDE) {
			return SectionContainer
		} else {
			return Section
		}
	}

	return sections.map((section, i) => {
		// Force best picks to the head, do not render section here
		if (section.type === SECTION_BEST_PICKS) {
			return ''
		}
		// Based on the section type, container should always be wrapped i <article> else <section>
		const StyledSection = determineSectionStyle(section.type)
		return (
			<StyledSection id={section.id} isFirstSection={i === 0} key={i}>
				{renderSection({ section, author, categoryWidgetId, slug })}
			</StyledSection>
		)
	})
}
